import { useState, useRef, useEffect, useCallback } from 'react';

const SCROLL_THRESHOLD = 5;
const DESKTOP_BP = 1024;

const isDesktop = () => window.innerWidth >= DESKTOP_BP;

const getScrollContainer = (standingsEl, viewEl) =>
  isDesktop()
    ? viewEl
    : standingsEl?.closest('.content_div');

/**
 * Gestiona todo el estado de scroll de WCStandings:
 * - subtabHidden: si el subtab bar (Fase de grupos | Ranking | Vista torneo) está colapsado
 * - viewScrolled: si el contenedor de scroll fue desplazado (scrollTop > 0)
 * - viewAtBottom: si el scroll del overlay llegó al fondo (solo desktop)
 *
 * También propaga `mundial2026--standings-scrolled` al ancestro .mundial2026
 * para que .wc-tabs__list pueda reaccionar con margin-bottom en mobile/tablet.
 */
export const useStandingsScroll = activeView => {
  const [subtabHidden, setSubtabHidden] = useState(false);
  const [viewScrolled, setViewScrolled] = useState(false);
  const [viewAtBottom, setViewAtBottom] = useState(true);
  const standingsRef = useRef(null);
  const viewRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  const resetScroll = useCallback(() => {
    setSubtabHidden(false);
    setViewScrolled(false);
    setViewAtBottom(true);
    lastScrollTopRef.current = 0;
    if (viewRef.current) viewRef.current.scrollTop = 0;
    const tabpanel = standingsRef.current?.closest('[data-role="mundial2026-tabpanel"]');
    if (tabpanel) tabpanel.scrollTop = 0;
    const contentDiv = standingsRef.current?.closest('.content_div');
    if (contentDiv) contentDiv.scrollTop = 0;
  }, []);

  // Propagar estado de scroll al ancestro .mundial2026
  useEffect(() => {
    const mundial = standingsRef.current?.closest('[data-role="mundial2026"]');
    if (!mundial) return;
    if (viewScrolled) {
      mundial.setAttribute('data-standings-scrolled', '');
    } else {
      mundial.removeAttribute('data-standings-scrolled');
    }
    return () => mundial.removeAttribute('data-standings-scrolled');
  }, [viewScrolled]);

  // Listener de scroll
  useEffect(() => {
    let container = getScrollContainer(standingsRef.current, viewRef.current);
    lastScrollTopRef.current = container?.scrollTop || 0;

    const handleScroll = () => {
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const delta = scrollTop - lastScrollTopRef.current;

      if (isDesktop()) {
        if (Math.abs(delta) > SCROLL_THRESHOLD) {
          if (delta > 0 && scrollTop > 50) setSubtabHidden(true);
          lastScrollTopRef.current = scrollTop;
        }
        if (scrollTop <= 0) setSubtabHidden(false);
      } else {
        setSubtabHidden(scrollTop > 0);
      }

      setViewScrolled(scrollTop > 0);

      if (isDesktop()) {
        setViewAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
      }
    };

    const handleResize = () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      container = getScrollContainer(standingsRef.current, viewRef.current);
      lastScrollTopRef.current = container?.scrollTop || 0;
      if (container)
        container.addEventListener('scroll', handleScroll, { passive: true });
    };

    // Recalcular viewAtBottom cuando el contenido cambia de tamaño (ej. cambio de ronda)
    let resizeObserver;
    if (container && isDesktop()) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const { scrollTop, scrollHeight, clientHeight } = container;
          setViewAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
        });
      });
      resizeObserver.observe(container);
    }

    if (container) {
      requestAnimationFrame(handleScroll);
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('resize', handleResize);

    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [activeView]);

  return {
    subtabHidden,
    viewScrolled,
    viewAtBottom,
    standingsRef,
    viewRef,
    resetScroll,
  };
};
